/**
 * AdminCarerEssentialsMigration — TEMPORARY admin-only control.
 *
 * One-off tool to run the carer_profile -> carer_essentials entitlement
 * migration (see backend/membership.py::admin_migrate_carer_essentials)
 * from inside the existing admin session, without ever sharing the admin
 * token outside the app. Dry-run first, real run only after review.
 *
 * Safe to delete this file + its route/AdminHome card once the migration
 * has been run and confirmed in Production — it has no other purpose.
 */
import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";

function EmailList({ emails }) {
  if (!emails || emails.length === 0) return <p className="text-sm text-ink-soft">None.</p>;
  return (
    <ul className="mt-1 space-y-1 text-sm text-ink">
      {emails.map((e) => (
        <li key={e} className="font-mono">{e}</li>
      ))}
    </ul>
  );
}

export default function AdminCarerEssentialsMigration() {
  const [dryRunResult, setDryRunResult] = useState(null);
  const [realResult, setRealResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmingReal, setConfirmingReal] = useState(false);
  const [error, setError] = useState("");

  const runDryRun = async () => {
    setLoading(true);
    setError("");
    setRealResult(null);
    try {
      const { data } = await api.post("/api/membership/admin/migrate-carer-essentials?dry_run=true");
      setDryRunResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const runReal = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/api/membership/admin/migrate-carer-essentials?dry_run=false");
      setRealResult(data);
      setConfirmingReal(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-carer-essentials-migration-page">
      <PageHero
        eyebrow="JCC Admin — temporary tool"
        title="Carer Essentials migration"
        description="One-off tool to move existing £7/month subscribers from the legacy carer_profile entitlement onto the dedicated carer_essentials entitlement. Selection is based on real native_membership_subscriptions records, not just the entitlement flag. Run the dry run first."
      />
      <section className="section pt-8">
        <div className="container-jcc max-w-3xl space-y-6">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="admin-ce-migration-error">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={runDryRun}
            disabled={loading}
            className="btn-outline"
            data-testid="admin-ce-migration-dry-run-btn"
          >
            {loading ? "Running dry run…" : "Run dry-run migration"}
          </button>

          {dryRunResult && (
            <div className="card-base p-6 space-y-4" data-testid="admin-ce-migration-dry-run-result">
              <p className="text-sm text-ink-muted">{dryRunResult.selection_method}</p>
              <div>
                <p className="font-semibold text-ink" data-testid="admin-ce-migration-would-migrate-count">
                  Would migrate: {dryRunResult.would_migrate_count}
                </p>
                <EmailList emails={dryRunResult.emails} />
              </div>
              <div>
                <p className="font-semibold text-amber-700" data-testid="admin-ce-migration-flagged-count">
                  Flagged for manual review: {dryRunResult.flagged_for_manual_review_count}
                </p>
                {dryRunResult.flagged_for_manual_review_count > 0 && (
                  <p className="text-xs text-ink-soft mt-1">{dryRunResult.flagged_reason}</p>
                )}
                <EmailList emails={dryRunResult.flagged_for_manual_review_emails} />
              </div>

              {!realResult && !confirmingReal && (
                <button
                  type="button"
                  onClick={() => setConfirmingReal(true)}
                  className="btn-purple"
                  data-testid="admin-ce-migration-run-real-btn"
                >
                  Run migration
                </button>
              )}

              {confirmingReal && !realResult && (
                <div className="rounded-xl border border-purple/30 bg-purple/5 p-4 space-y-3" data-testid="admin-ce-migration-confirm-box">
                  <p className="text-sm text-ink">
                    This will grant carer_essentials to the {dryRunResult.would_migrate_count} account(s) listed above.
                    This does not create charges, does not touch Stripe subscriptions, and never removes carer_profile.
                  </p>
                  <div className="flex gap-3">
                    <button type="button" onClick={runReal} disabled={loading} className="btn-purple" data-testid="admin-ce-migration-confirm-btn">
                      {loading ? "Running…" : "Yes, run the real migration"}
                    </button>
                    <button type="button" onClick={() => setConfirmingReal(false)} className="btn-outline" data-testid="admin-ce-migration-cancel-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {realResult && (
            <div className="card-base p-6 space-y-3" data-testid="admin-ce-migration-real-result">
              <p className="font-semibold text-teal" data-testid="admin-ce-migration-migrated-count">
                Migrated: {realResult.migrated_count}
              </p>
              <EmailList emails={realResult.emails} />
              <p className="text-xs text-ink-soft">{realResult.note}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
